@if (visible) {
  <section
    class="bg-white rounded-xl p-4 shadow-sm border border-slate-200/70"
    data-testid="agreement-card"
    aria-labelledby="agreement-card-heading"
  >
    <h3
      id="agreement-card-heading"
      class="text-xs font-bold uppercase tracking-wider text-slate-500"
    >
      Reviewer Agreement
    </h3>
    @if (agreement && isOfficial) {
      <p class="mt-1 text-[11px] text-slate-500">Official pre-moderation result</p>
    } @else if (agreement) {
      <p class="mt-1 text-[11px] text-slate-500">Preliminary result</p>
    }

    @if (agreement) {
      <span
        class="mt-2 inline-block rounded-md border px-2 py-1 text-[11px] font-semibold"
        [ngClass]="{
          'border-emerald-200 bg-emerald-50 text-emerald-700':
            agreement.thresholdStatus === 'MEETS_THRESHOLD',
          'border-amber-200 bg-amber-50 text-amber-800':
            agreement.thresholdStatus === 'BELOW_THRESHOLD',
          'border-slate-200 bg-slate-50 text-slate-600':
            agreement.thresholdStatus !== 'MEETS_THRESHOLD' &&
            agreement.thresholdStatus !== 'BELOW_THRESHOLD',
        }"
      >
        {{ thresholdStatusLabel() }}
      </span>
    }

    @if (loading) {
      <div class="mt-4 animate-pulse space-y-3" data-testid="agreement-loading">
        <div class="h-8 w-24 rounded bg-slate-200"></div>
        <div class="h-4 w-40 rounded bg-slate-100"></div>
        <div class="h-4 w-full rounded bg-slate-100"></div>
      </div>
    } @else if (errorMessage) {
      <div class="mt-4 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
        <p>{{ errorMessage }}</p>
        <button
          type="button"
          class="mt-2 rounded-md border border-red-300 bg-white px-3 py-1 text-xs font-semibold text-red-700 hover:bg-red-100"
          (click)="retry()"
        >
          Retry
        </button>
      </div>
    } @else if (agreement) {
      <div class="mt-3 flex items-center justify-between gap-2">
        <div>
          @if (agreement.weightedPairwiseKappa !== null) {
            <div class="flex items-baseline gap-1.5">
              <span class="text-2xl font-extrabold text-slate-900">
                &kappa; {{ formatKappa(agreement.weightedPairwiseKappa) }}
              </span>
            </div>
            <p class="mt-0.5 text-xs font-semibold text-slate-600">
              {{ interpretationLabel() }}
            </p>
          } @else {
            <p class="text-xs font-semibold text-slate-800">{{ interpretationLabel() }}</p>
            @if (reasonLabel()) {
              <p class="mt-0.5 text-xs leading-relaxed text-slate-500">{{ reasonLabel() }}</p>
            }
          }
        </div>

        <button
          type="button"
          class="shrink-0 text-xs font-semibold text-secondary hover:underline focus:outline-none focus:ring-2 focus:ring-secondary/40 rounded"
          (click)="detailsOpen = true"
        >
          View details
        </button>
      </div>
    }
  </section>
}

@if (agreement) {
  <app-base-modal
    [open]="detailsOpen"
    (openChange)="detailsOpen = $event"
    title="Reviewer Agreement Details"
    size="xl"
  >
    <div class="space-y-5" data-testid="agreement-details-dialog">
      <div class="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p class="text-xs font-semibold uppercase tracking-wide text-slate-500">
            {{ stageLabel() }}
          </p>
          <h3 class="text-xl font-bold text-slate-900">
            {{
              agreement.calculationType === 'OFFICIAL'
                ? 'Official pre-moderation result'
                : 'Preliminary result'
            }}
          </h3>
          @if (isOfficial && finalizedAt) {
            <p class="mt-1 text-sm text-slate-500">
              Finalized on {{ finalizedAt }} by {{ finalizedByUserName }}
            </p>
          }
        </div>
        <span
          class="rounded-md border border-slate-200 bg-slate-50 px-3 py-1 text-xs font-semibold text-slate-700"
        >
          {{ thresholdStatusLabel() }}
        </span>
      </div>

      @if (reasonLabel()) {
        <div class="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
          {{ reasonLabel() }}
        </div>
      }

      <dl class="grid grid-cols-2 gap-3 md:grid-cols-4">
        <div class="rounded-lg border border-slate-200 bg-slate-50 p-3">
          <dt class="text-[11px] font-semibold uppercase tracking-wide text-slate-400">
            Paired decisions
          </dt>
          <dd class="text-lg font-bold text-slate-900">{{ agreement.pairedDecisionCount }}</dd>
        </div>
        <div class="rounded-lg border border-slate-200 bg-slate-50 p-3">
          <dt class="text-[11px] font-semibold uppercase tracking-wide text-slate-400">
            Agreements
          </dt>
          <dd class="text-lg font-bold text-slate-900">{{ agreement.agreementCount }}</dd>
        </div>
        <div class="rounded-lg border border-slate-200 bg-slate-50 p-3">
          <dt class="text-[11px] font-semibold uppercase tracking-wide text-slate-400">
            Disagreements
          </dt>
          <dd class="text-lg font-bold text-slate-900">{{ agreement.disagreementCount }}</dd>
        </div>
        <div class="rounded-lg border border-slate-200 bg-slate-50 p-3">
          <dt class="text-[11px] font-semibold uppercase tracking-wide text-slate-400">
            Unpaired / missing
          </dt>
          <dd class="text-lg font-bold text-slate-900">
            {{ agreement.unpairedItemCount }} / {{ agreement.missingDecisionCount }}
          </dd>
        </div>
        <div class="rounded-lg border border-slate-200 bg-slate-50 p-3">
          <dt class="text-[11px] font-semibold uppercase tracking-wide text-slate-400">
            Percent agreement
          </dt>
          <dd class="text-lg font-bold text-slate-900">
            {{ formatPercent(agreement.overallPercentAgreement) }}
          </dd>
        </div>
        <div class="rounded-lg border border-slate-200 bg-slate-50 p-3">
          <dt class="text-[11px] font-semibold uppercase tracking-wide text-slate-400">
            Weighted kappa
          </dt>
          <dd class="text-lg font-bold text-slate-900">
            {{ formatKappa(agreement.weightedPairwiseKappa) }}
          </dd>
        </div>
        <div class="rounded-lg border border-slate-200 bg-slate-50 p-3">
          <dt class="text-[11px] font-semibold uppercase tracking-wide text-slate-400">
            Interpretation
          </dt>
          <dd class="text-lg font-bold text-slate-900">{{ interpretationLabel() }}</dd>
        </div>
        <div class="rounded-lg border border-slate-200 bg-slate-50 p-3">
          <dt class="text-[11px] font-semibold uppercase tracking-wide text-slate-400">
            Threshold
          </dt>
          <dd class="text-lg font-bold text-slate-900">
            {{ formatThreshold(agreement.configuredThreshold) }}
          </dd>
        </div>
      </dl>

      <div class="overflow-x-auto rounded-lg border border-slate-200">
        <table class="min-w-full divide-y divide-slate-200 text-sm">
          <thead
            class="bg-slate-50 text-left text-xs font-semibold uppercase tracking-wide text-slate-500"
          >
            <tr>
              <th class="px-3 py-2">Reviewer pair</th>
              <th class="px-3 py-2">Paired items</th>
              <th class="px-3 py-2">Agreements</th>
              <th class="px-3 py-2">Disagreements</th>
              <th class="px-3 py-2">Percent agreement</th>
              <th class="px-3 py-2">Cohen's kappa</th>
              <th class="px-3 py-2">Interpretation</th>
              <th class="px-3 py-2">Threshold status</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-slate-100 bg-white">
            @for (pair of agreement.reviewerPairs; track trackPair($index, pair)) {
              <tr>
                <td class="px-3 py-2 font-semibold text-slate-800">
                  {{ reviewerPairLabel(pair) }}
                </td>
                <td class="px-3 py-2">{{ pair.pairedItemCount }}</td>
                <td class="px-3 py-2">{{ pair.agreementCount }}</td>
                <td class="px-3 py-2">{{ pair.disagreementCount }}</td>
                <td class="px-3 py-2">{{ formatPercent(pair.percentAgreement) }}</td>
                <td class="px-3 py-2">{{ formatKappa(pair.cohenKappa) }}</td>
                <td class="px-3 py-2">{{ interpretationLabel(pair.interpretation) }}</td>
                <td class="px-3 py-2">
                  {{ thresholdStatusLabel(pair.thresholdStatus) }}
                </td>
              </tr>
            } @empty {
              <tr>
                <td class="px-3 py-4 text-slate-500" colspan="8">
                  No paired reviewer comparisons are available.
                </td>
              </tr>
            }
          </tbody>
        </table>
      </div>
    </div>
  </app-base-modal>
}
